// src/store/modules/auth.js
import apiService from '@/services/api.service';
import socketService from '@/services/socket.service';
import authService from '@/services/auth.service';
import { enregistrerLangueCompte } from '@/i18n/compte';

const state = {
  token: authService.getToken() || '',
  user: authService.getUser() || {},
  loading: false,
  error: null
};

const getters = {
  isAuthenticated: () => authService.isAuthenticated(),
  isAdmin: () => authService.isAdmin(),
  isClient: () => authService.isClient(),
  token: state => state.token,
  user: state => state.user,
  loading: state => state.loading,
  error: state => state.error
};

const actions = {
  // Établir la session à partir d'une réponse déjà obtenue.
  //
  // La connexion se fait maintenant en DEUX temps : le jeton d'accès n'arrive plus au bout de
  // `login`, mais au bout de la vérification du second facteur — ou de son enrôlement. Cette
  // action est le point unique où une session s'ouvre, quel que soit le chemin emprunté.
  etablirSession({ commit }, { token, user }) {
    authService.setToken(token, user);
    // Nouvelle identité : la connexion temps réel ouverte porte l'ANCIEN jeton (ou aucun).
    socketService.disconnect();
    commit('setToken', token);
    commit('setUser', user);
    commit('clearError');
    // La langue choisie avant de se connecter devient celle du compte (client, membre).
    enregistrerLangueCompte();
    return user;
  },

  // Connexion admin
  async login({ commit }, credentials) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const response = await apiService.login(credentials);
      const { token, user } = response.data;
      
      // Enregistrer le token et les données utilisateur
      authService.setToken(token, user);
      if (token) socketService.disconnect();   // connexion temps réel à rouvrir avec ce jeton
      
      commit('setToken', token);
      commit('setUser', user);
      commit('setLoading', false);
      
      return user;
    } catch (error) {
      commit('setError', error.response ? error.response.data.message : 'Erreur de connexion');
      commit('setLoading', false);
      throw error;
    }
  },
  
  // Connexion client
  async loginClient({ commit }, credentials) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const response = await apiService.loginClient(credentials);
      const { token, user } = response.data;
      
      // Enregistrer le token et les données utilisateur
      authService.setToken(token, user);
      if (token) socketService.disconnect();   // connexion temps réel à rouvrir avec ce jeton
      
      commit('setToken', token);
      commit('setUser', user);
      commit('setLoading', false);
      
      return user;
    } catch (error) {
      commit('setError', error.response ? error.response.data.message : 'Erreur de connexion');
      commit('setLoading', false);
      throw error;
    }
  },
  
  // Changement de mot de passe
  async changePassword({ commit }, passwords) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const r = await apiService.changePassword(passwords);
    // Le serveur a coupé les autres sessions et rend un jeton neuf pour celle-ci (02/10/2026) :
    // on le garde, et la connexion temps réel (coupée elle aussi) repart avec lui.
    if (r && r.data && r.data.token) {
      authService.setToken(r.data.token, authService.getUser());
      commit('setToken', r.data.token);
      socketService.reconnecter();
    }
      commit('setLoading', false);
    } catch (error) {
      commit('setError', error.response ? error.response.data.message : 'Erreur de changement de mot de passe');
      commit('setLoading', false);
      throw error;
    }
  },



// Changement de mot de passe pour les clients
async changeClientPassword({ commit }, passwords) {
  commit('setLoading', true);
  commit('clearError');
  
  try {
    const r = await apiService.changeClientPassword(passwords);
    // Le serveur a coupé les autres sessions et rend un jeton neuf pour celle-ci (02/10/2026) :
    // on le garde, et la connexion temps réel (coupée elle aussi) repart avec lui.
    if (r && r.data && r.data.token) {
      authService.setToken(r.data.token, authService.getUser());
      commit('setToken', r.data.token);
      socketService.reconnecter();
    }
    commit('setLoading', false);
  } catch (error) {
    commit('setError', error.response ? error.response.data.message : 'Erreur de changement de mot de passe');
    commit('setLoading', false);
    throw error;
  }
},



  
  // Déconnexion
  logout({ commit }) {
    // Supprimer le token et les données utilisateur
    authService.clearToken();
    socketService.disconnect();
    
    commit('clearToken');
    commit('clearUser');
  }
};

const mutations = {
  setToken(state, token) {
    state.token = token;
  },
  setUser(state, user) {
    state.user = user;
  },
  setLoading(state, status) {
    state.loading = status;
  },
  setError(state, error) {
    state.error = error;
  },
  clearError(state) {
    state.error = null;
  },
  clearToken(state) {
    state.token = '';
  },
  clearUser(state) {
    state.user = {};
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
